// src/shared/ui/statusMaps.js
//
// Central registry of status -> { label, icon, className/color } lookups.
// Every module used to define its own copy of this pattern (compliance
// JobBadges, golive RunStatusBadge, adai history StatusBadge, rds_ui
// StatusBadge/PriorityBadge, IT StatusPill, the root coverage StatusBadge).
// They're consolidated here so <StatusBadge> (see StatusBadge.jsx) can
// render any of them from one component — add a new module's vocabulary
// by adding a map here, not by writing a new badge component.
//
// Each entry maps a raw status string to:
//   label      - display text (falls back to the raw status if omitted)
//   icon       - optional lucide-react component
//   className  - optional Tailwind utility classes (background + text)
//   color      - optional plain CSS class name, for modules still on
//                hand-written CSS (e.g. IT's it-pill / it-{status})
//
// A map may also export `resolve(status)` for non-trivial normalization
// (e.g. the coverage badge collapses "partially_covered" and "partial"
// to the same entry).

import {
  CheckCircle2,
  AlertCircle,
  XCircle,
  Clock,
  Loader,
  Key,
  Play,
  RefreshCw,
  CheckCircle,
  FileText,
  Shield,
  TrendingUp,
  Scale,
  Globe,
  Building2,
  Briefcase,
  FolderGit2,
  ListChecks,
  Link2,
} from "lucide-react";

/** root/coverage badges — requirement coverage state */
export const coverageStatusMap = {
  covered: { label: "Covered", icon: CheckCircle2, className: "badge-covered" },
  partiallycovered: { label: "Partial", icon: AlertCircle, className: "badge-partial" },
  partial: { label: "Partial", icon: AlertCircle, className: "badge-partial" },
  missing: { label: "Missing", icon: XCircle, className: "badge-missing" },
};

/** GoLive deployment run status (10-state pipeline) */
export const goliveStatusMap = {
  created: { label: "Created", icon: Clock },
  awaiting_deploy_key: { label: "Setup Required", icon: Key },
  analyzing: { label: "Analyzing", icon: Loader },
  awaiting_review: { label: "Awaiting Review", icon: Clock },
  generating_script: { label: "Generating Script", icon: Loader },
  ready_to_execute: { label: "Ready to Execute", icon: Play },
  regenerating_script: { label: "Regenerating Script", icon: RefreshCw },
  executing: { label: "Executing", icon: Loader },
  succeeded: { label: "Succeeded", icon: CheckCircle },
  failed: { label: "Failed", icon: XCircle },
};

/** Compliance job status (RAID / TVR / SMP pipelines) */
export const complianceJobStatusMap = {
  queued: { label: "Queued", icon: Clock, className: "bg-[#f1f5f9] text-[#475569]" },
  running: { label: "Running", icon: Loader, className: "bg-[#dbeafe] text-[#2563eb]" },
  completed: { label: "Completed", icon: CheckCircle, className: "bg-[#dcfce7] text-[#16a34a]" },
  failed: { label: "Failed", icon: XCircle, className: "bg-[#fee2e2] text-[#dc2626]" },
};

/** Compliance job type (which pipeline produced this job) */
export const complianceTypeMap = {
  smp: { label: "SMP", icon: FileText, className: "bg-[#dbeafe] text-[#2563eb]" },
  raid: { label: "RAID", icon: Shield, className: "bg-[#e0e7ff] text-[#4338ca]" },
  tvr: { label: "TVR", icon: TrendingUp, className: "bg-[#d1fae5] text-[#065f46]" },
  obligation: { label: "Obligations", icon: Scale, className: "bg-[#fef3c7] text-[#b45309]" },
  stm: { label: "STM", icon: ListChecks, className: "bg-[#fef3c7] text-[#92400e]" },
  rtm: { label: "RTM", icon: Link2, className: "bg-[#ede9fe] text-[#6d28d9]" },
};

/** ADAI documentation-generation job status — keys match DocumentStatus's
 *  real wire values (domain/adai/value_objects/document_status.py), not the
 *  queued/running placeholders this map used before. */
export const adaiJobStatusMap = {
  pending: { label: "Pending", className: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress", className: "bg-blue-100 text-blue-600" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700" },
  failed: { label: "Failed", className: "bg-red-100 text-red-600" },
};

/** RDS generation-run / SOW status (pipeline + document lifecycle) */
export const rdsStatusMap = {
  pending: { label: "Pending", className: "bg-gray-100 text-gray-500 border-gray-200" },
  queued: { label: "Queued", className: "bg-gray-100 text-gray-600 border-gray-200" },
  extracting_modules: { label: "Extracting Modules", className: "bg-blue-100 text-blue-700 border-blue-200" },
  generating_artifacts: { label: "Generating Artifacts", className: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  completed: { label: "Completed", className: "bg-green-100 text-green-700 border-green-200" },
  failed: { label: "Failed", className: "bg-red-100 text-red-700 border-red-200" },
  uploaded: { label: "Uploaded", className: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  parsed: { label: "Parsed", className: "bg-green-100 text-green-700 border-green-200" },
  parsing: { label: "Parsing", className: "bg-blue-100 text-blue-700 border-blue-200" },
};

/** RDS artifact/risk priority */
export const priorityMap = {
  critical: { label: "Critical", className: "bg-red-200 text-red-800 border-red-300" },
  high: { label: "High", className: "bg-red-100 text-red-700 border-red-200" },
  medium: { label: "Medium", className: "bg-orange-100 text-orange-700 border-orange-200" },
  low: { label: "Low", className: "bg-green-100 text-green-700 border-green-200" },
};

/** Connections registration status (LLM/source-control/work-item/CRM connections) */
export const connectionStatusMap = {
  untested: { label: "Untested", icon: Clock, className: "bg-gray-100 text-gray-500 border-gray-200" },
  testing: { label: "Testing…", icon: Loader, className: "bg-blue-100 text-blue-700 border-blue-200" },
  active: { label: "Active", icon: CheckCircle2, className: "bg-green-100 text-green-700 border-green-200" },
  failed: { label: "Failed", icon: XCircle, className: "bg-red-100 text-red-700 border-red-200" },
};

/** Real Conductor `EngagementStage` values (domain/conductor/value_objects/engagement_stage.py). */
export const engagementStageMap = {
  sow: { label: "SOW", icon: Clock, className: "bg-gray-100 text-gray-600 border-gray-200" },
  rds: { label: "RDS", icon: Clock, className: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  codebase: { label: "Codebase", icon: Loader, className: "bg-blue-100 text-blue-700 border-blue-200" },
  golive: { label: "GoLive", icon: Loader, className: "bg-purple-100 text-purple-700 border-purple-200" },
  compliance: { label: "Compliance", icon: AlertCircle, className: "bg-orange-100 text-orange-700 border-orange-200" },
  docs: { label: "Docs", icon: CheckCircle, className: "bg-teal-100 text-teal-700 border-teal-200" },
  closed: { label: "Closed", icon: CheckCircle2, className: "bg-green-100 text-green-700 border-green-200" },
};

/** Account `is_active` flag (AccountResponse.is_active). */
export const accountStatusMap = {
  active: { label: "Active", icon: CheckCircle2, className: "bg-green-100 text-green-700 border-green-200" },
  inactive: { label: "Inactive", icon: XCircle, className: "bg-gray-100 text-gray-500 border-gray-200" },
};

/** Generic catalog-record activation state — the is_active flag on Users,
 *  Roles and Permissions (admin/ pages). Every one of those lists rendered
 *  it as bare "Yes"/"No" text or a local .badge class before. */
export const recordStatusMap = {
  active: { label: "Active", icon: CheckCircle2, className: "bg-green-100 text-green-700 border-green-200" },
  inactive: { label: "Inactive", icon: XCircle, className: "bg-gray-100 text-gray-500 border-gray-200" },
};

/** Role attachment level — domain/identity/value_objects/role_level.py's
 *  RoleLevel. Exactly one per role, never combined, and fixed once created.
 *  Ordered widest-scope to narrowest, which is also how the colours read. */
export const roleLevelMap = {
  platform: { label: "Platform", icon: Globe, className: "bg-purple-100 text-purple-700 border-purple-200" },
  org: { label: "Organization", icon: Building2, className: "bg-blue-100 text-blue-700 border-blue-200" },
  account: { label: "Account", icon: Briefcase, className: "bg-teal-100 text-teal-700 border-teal-200" },
  project: { label: "Project", icon: FolderGit2, className: "bg-amber-100 text-amber-800 border-amber-200" },
};

/** IT service-desk request status — still on hand-written CSS (it-pill / it-{status}) */
export const itRequestStatusMap = {
  pending_manager: { label: "Pending Manager", color: "it-pending_manager" },
  approved: { label: "Approved", color: "it-approved" },
  rejected: { label: "Rejected", color: "it-rejected" },
  cancelled: { label: "Cancelled", color: "it-cancelled" },
};

/** which statuses should pulse/animate (used by rdsStatusMap consumers) */
export const rdsAnimatedStatuses = new Set(["generating_artifacts", "extracting_modules"]);

/** Registry so <StatusBadge map="golive" status="..." /> works without importing the map directly. */
export const STATUS_MAP_REGISTRY = {
  coverage: coverageStatusMap,
  golive: goliveStatusMap,
  complianceJob: complianceJobStatusMap,
  complianceType: complianceTypeMap,
  adai: adaiJobStatusMap,
  rds: rdsStatusMap,
  priority: priorityMap,
  it: itRequestStatusMap,
  connection: connectionStatusMap,
  engagementStage: engagementStageMap,
  accountStatus: accountStatusMap,
  record: recordStatusMap,
  roleLevel: roleLevelMap,
};

/** Normalizes a raw status string the same way the old coverage badge did:
 *  lower-cased, with separators stripped, so "Partially Covered" / "partially-covered"
 *  / "partially_covered" all resolve to the same map entry. */
export function normalizeStatusKey(status) {
  return String(status || "").toLowerCase().replace(/[_\s-]+/g, "");
}
